import {
  Checkbox, Icon, Select, Tooltip, Typography,
} from '@gupshup-frontend/gupshup-ts-components';
import { isAuthentication, isUtility } from '../state';
import { friendlyError } from './errorMessages';

// Category-specific copy, transcribed verbatim from the console (gs-ui-templates' en.json).
// AUTH_VALIDITY_TEXT is the always-visible sub-header text (helperTextFor below) — the header
// TOOLTIP (Authentication only, see AUTH_HEADER_TOOLTIP_TEXT) uses its own, different console
// string; the two used to be conflated here, but MessageValidity/index.tsx keeps them apart
// (its own "message_validity_period" Tooltip vs. its renderValiditySubText()).
const AUTH_VALIDITY_TEXT = "It's recommended to set a custom validity period that your "
  + 'authentication message must be delivered by before it expires. If a message is not '
  + 'delivered within this timeframe, you will not be charged and your customer will not see '
  + 'the message.';

const UTILITY_VALIDITY_TEXT = 'You can set a custom validity period that your utility message '
  + 'must be delivered by before it expires. If a message is not delivered within this '
  + 'timeframe, you will not be charged and your customer will not see the message.';

// The console appends a "Check here" hyperlink after this sentence; we have no destination URL
// for it, so the link is omitted and the sentence stands alone.
const MARKETING_VALIDITY_TEXT = 'You can set a custom validity period that your marketing '
  + 'message must be delivered by before it expires. If a message is not delivered within this '
  + 'time frame, you will not be charged and your customer will not see the message. Applicable '
  + 'only if WABA is Onboarded on MM Lite.';

// Header tooltip (Authentication only) — console-verbatim (en.json's
// the_message_validity_period_is_different_from_expiry), distinct from AUTH_VALIDITY_TEXT above.
const AUTH_HEADER_TOOLTIP_TEXT = 'The message validity period is different from expiry time for '
  + 'the code. Code expiry is content that you can add to your message so that customers know '
  + 'when the OTP passcode has expired.';

// Info tooltip beside the Select (Authentication only) — console's AuthValidityText
// (Common/Static.tsx), three en.json-verbatim paragraphs separated by a blank line each.
const AUTH_TOOLTIP_PARAGRAPH_1 = "The message validity period can expire if your customer's "
  + 'WhatsApp account is unreachable. You can check your webhook for message delivery status '
  + 'after the validity period has passed.';
const AUTH_TOOLTIP_PARAGRAPH_2 = 'Make sure that you have a backup delivery method for your '
  + "authentication message in case your customer's WhatsApp account is unreachable.";
const AUTH_TOOLTIP_PARAGRAPH_3 = 'You should only set a low validity period if you have '
  + 'automatic backup delivery methods in place.';

// Console's messageValidityIcon() (MORE_INFO, constants/icons.ts) — a gs-upload hosted SVG, same
// hosting pattern sections/Body.js already uses for its toolbar icons.
const AUTH_SELECT_TOOLTIP_ICON = 'https://gs-upload.gupshup.io/templates/icons/ic_info_default.svg';

// Checkbox caption pieces (kept out of raw JSX text so the apostrophe in "don't" never has to be
// escaped) — console-verbatim (en.json's if_you_don't_set_custom_validity), with <strong> wrapping
// only the duration, same as the console's own <strong> component.
const VALIDITY_CHECKBOX_CAPTION_PREFIX = "If you don't set a custom validity period, the standard ";
const VALIDITY_CHECKBOX_CAPTION_SUFFIX = ' WhatsApp message validity period will be applied.';

// Minutes-value option lists per category, copied verbatim from gs-ui-templates'
// src/containers/Whatsapp/CreateTemplate/config.ts:410-432 (AUTH_MESSAGE_VALIDITY /
// UTILITY_MESSAGE_VALIDITY / MARKETING_MESSAGE_VALIDITY). Do not re-derive or alter these — each
// `value` is exactly what state.messageValidity.validityPeriod.value must hold, since Form/
// payload.js reads it as minutes and multiplies by 60 to build messageSendTtlSeconds.
const AUTH_MESSAGE_VALIDITY = [
  { value: '0.5', label: '30 seconds' },
  { value: '1', label: '1 minute' },
  { value: '2', label: '2 minutes' },
  { value: '3', label: '3 minutes' },
  { value: '5', label: '5 minutes' },
  { value: '10', label: '10 minutes' },
  { value: '15', label: '15 minutes' },
];

const UTILITY_MESSAGE_VALIDITY = [
  { value: '0.5', label: '30 seconds' },
  { value: '1', label: '1 minute' },
  { value: '2', label: '2 minutes' },
  { value: '5', label: '5 minutes' },
  { value: '10', label: '10 minutes' },
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '180', label: '3 hours' },
  { value: '360', label: '6 hours' },
  { value: '720', label: '12 hours' },
];

const MARKETING_MESSAGE_VALIDITY = [
  { value: '720', label: '12 hours' },
  { value: '1440', label: '24 hours' },
  { value: '2880', label: '2 days' },
  { value: '4320', label: '3 days' },
  { value: '10080', label: '7 days' },
  { value: '21600', label: '15 days' },
  { value: '43200', label: '30 days' },
];

// Console-verbatim preselect values (config.ts's AUTH_DEFAULT_VALIDITY/UTILITY_DEFAULT_VALIDITY/
// MARKETING_DEFAULT_VALIDITY), minutes — each one is present in its own category's option list
// above. Applied the moment the checkbox is ticked with no valid value yet; see
// handleCheckedChange below.
const AUTH_DEFAULT_VALIDITY = '10';
const UTILITY_DEFAULT_VALIDITY = '30';
const MARKETING_DEFAULT_VALIDITY = '43200';

// isMarketing/isUtility/isAuthentication are mutually exclusive (state.js's category.value is a
// single string) — marketing is the fallback purely because it's checked last, matching the
// console's own equivalent precedence.
function optionsFor(state) {
  if (isAuthentication(state)) return AUTH_MESSAGE_VALIDITY;
  if (isUtility(state)) return UTILITY_MESSAGE_VALIDITY;
  return MARKETING_MESSAGE_VALIDITY;
}

// Same category precedence as optionsFor above.
function defaultValidityFor(state) {
  if (isAuthentication(state)) return AUTH_DEFAULT_VALIDITY;
  if (isUtility(state)) return UTILITY_DEFAULT_VALIDITY;
  return MARKETING_DEFAULT_VALIDITY;
}

// Builds a label for a validityPeriod value that isn't one of the fixed presets above, using the
// same unit ladder those presets already read in (seconds under a minute, then whole minutes,
// hours, or days) so an appended fallback option reads like it belongs in the list instead of
// showing a bare number of minutes. Falls back to plain minutes when the value doesn't land on a
// whole hour/day boundary — e.g. a 7-minute TTL, which isn't a preset in any category's list.
function formatValidityMinutesLabel(minutesValue) {
  const minutes = Number(minutesValue);
  if (!Number.isFinite(minutes) || minutes <= 0) return `${minutesValue} minutes`;
  if (minutes < 1) {
    const seconds = Math.round(minutes * 60);
    return `${seconds} second${seconds === 1 ? '' : 's'}`;
  }
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

// Same category precedence as optionsFor above.
function helperTextFor(state) {
  if (isAuthentication(state)) return AUTH_VALIDITY_TEXT;
  if (isUtility(state)) return UTILITY_VALIDITY_TEXT;
  return MARKETING_VALIDITY_TEXT;
}

// "Standard WhatsApp validity" wording for the checkbox's caption — independent of
// AUTH_DEFAULT_VALIDITY/UTILITY_DEFAULT_VALIDITY/MARKETING_DEFAULT_VALIDITY above (those preselect
// a value in the Select once ticked; this describes what WhatsApp applies when nothing is
// customized at all). Console-verbatim (config.ts's DEFAULT_AUTH_MSG_VALIDITY="10" minutes vs.
// DEFAULT_MSG_VALIDITY="30" days, the latter shared by both Marketing and Utility).
function standardValidityLabelFor(state) {
  return isAuthentication(state) ? '10 minutes' : '30 days';
}

// Message validity period — a checkbox that, once ticked, reveals a category-appropriate Select
// of fixed minute values (see payload.js's messageSendTtlSeconds). Hidden before any category is
// picked; offered for every category regardless of MM Lite eligibility (product decision
// 2026-09-24) — see the comment below.
const MessageValiditySection = ({ state, dispatch, errors, disabled }) => {
  if (!state.category.value) {
    return null;
  }

  // Marketing's validity period is offered to every account (product decision 2026-09-24: a
  // feature gated behind an Enterprise account flag is offered to everyone, as long as an account
  // without the flag gets a clear error at create time) — Enterprise rejects a non-MM-Lite account
  // with "The message ttl is not allowed for marketing category" (ce-api
  // ValidationUtils.java:1166-1186) before the Meta call, and this form already surfaces that
  // error text (Alert + toast) same as any other create/update failure.

  const { validityPeriod, isValidityChecked } = state.messageValidity;
  const presetOptions = optionsFor(state);
  const options = presetOptions.map((item) => ({
    ...item,
    'data-testid': `bloomreach-templates-form-validity-option-${item.value}`,
  }));
  // prefill.js sets this from message_send_ttl_seconds / 60 — the backend DTO carries no @IsIn,
  // so an existing template's TTL need not be one of this category's fixed presets above. Without
  // this, the Select's own renderValue looks `value` up in `items` and finds nothing, rendering
  // blank instead of the real value. Append it as an extra, SELECTABLE option (unlike the
  // disabled-fallback pattern Header.js/Buttons.js use for a type the form can no longer create) —
  // a non-preset TTL isn't a legacy shape being phased out, it's a normal value the user should
  // still be able to keep on save.
  const hasPresetValue = presetOptions.some((item) => item.value === validityPeriod.value);
  if (validityPeriod.value && !hasPresetValue) {
    options.push({
      value: validityPeriod.value,
      label: formatValidityMinutesLabel(validityPeriod.value),
      'data-testid': `bloomreach-templates-form-validity-option-${validityPeriod.value}`,
    });
  }
  const submitError = errors['messageValidity.validityPeriod.value'];
  const showValidityTooltip = isAuthentication(state);

  const handleCheckedChange = (event) => {
    const { checked } = event.target;
    // Bug fix: ticking with no value yet (or one left over from before a category reset) used to
    // sail right through to submit, which then failed with "Validity period is required" since
    // nothing had ever seeded one. Applied only at the moment of ticking — never against an
    // already-checked prefilled edit, whose value is always real (prefill.js only sets
    // isValidityChecked when the source template carried a real message_send_ttl_seconds) — so a
    // saved non-preset TTL (the fallback option above) is never silently overwritten here.
    const hasValidValue = presetOptions.some((item) => item.value === validityPeriod.value);
    dispatch({
      type: 'set',
      path: 'messageValidity',
      value: {
        isValidityChecked: checked,
        validityPeriod: (checked && !hasValidValue)
          ? { value: defaultValidityFor(state), error: '' }
          : validityPeriod,
      },
    });
  };

  const handleSelectChange = (selected) => {
    const value = Array.isArray(selected) ? selected[0] : selected;
    dispatch({ type: 'merge', path: 'messageValidity.validityPeriod', value: { value, error: '' } });
  };

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-validity">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-validity-heading">
            Message validity period
          </Typography>
        </div>
        {showValidityTooltip && (
          <Tooltip
            placement="left"
            title={AUTH_HEADER_TOOLTIP_TEXT}
            data-testid="bloomreach-templates-form-validity-tooltip"
          >
            <span>
              <Icon name="question" size={18} showCursor />
            </span>
          </Tooltip>
        )}
      </div>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        {helperTextFor(state)}
      </Typography>

      <Checkbox
        label="Set Custom validity period"
        checked={isValidityChecked}
        onChange={handleCheckedChange}
        disabled={disabled}
        data-testid="bloomreach-templates-form-validity-checkbox"
      />
      <Typography
        variant="caption"
        className="br-tpl-form-helper-text"
        data-testid="bloomreach-templates-form-validity-checkbox-caption"
      >
        {VALIDITY_CHECKBOX_CAPTION_PREFIX}
        <strong>{standardValidityLabelFor(state)}</strong>
        {VALIDITY_CHECKBOX_CAPTION_SUFFIX}
      </Typography>

      {isValidityChecked && (
        <div className="br-tpl-form-subcategory">
          <div className="br-tpl-form-inline-row">
            <div className="br-tpl-form-validity-select">
              <Select
                // Console has this typo verbatim (gs-ui-templates en.json's set_validity_period key
                // is literally "Set validty period"). Parity rule is to match the console except
                // where it has a bug — a misspelled label is a bug, not a wire contract, so we fix it
                // here instead of copying it forward.
                label="Set validity period"
                items={options}
                value={validityPeriod.value}
                handleChange={handleSelectChange}
                disabled={disabled}
                width="100%"
                data-testid="bloomreach-templates-form-validity-select"
              />
            </div>
            {showValidityTooltip && (
              <Tooltip
                placement="top"
                title={(
                  <>
                    {AUTH_TOOLTIP_PARAGRAPH_1}
                    <br />
                    <br />
                    {AUTH_TOOLTIP_PARAGRAPH_2}
                    <br />
                    <br />
                    {AUTH_TOOLTIP_PARAGRAPH_3}
                  </>
                )}
                data-testid="bloomreach-templates-form-validity-select-tooltip"
              >
                <span>
                  <Icon customPath={AUTH_SELECT_TOOLTIP_ICON} size={20} alt="" />
                </span>
              </Tooltip>
            )}
          </div>
          {submitError && (
            <Typography variant="caption" color="error" data-testid="bloomreach-templates-form-validity-error">
              {friendlyError(submitError)}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageValiditySection;
